/**
 * Catalogo tipado de observaciones (F2.1, plan maestro §5.3).
 *
 * Cada observacion tiene un codigo, una forma de valor, unidades EXPLICITAS y, si
 * aplica, lateralidad. La alternativa —una coleccion de campos libres— es la que
 * el plan pide evitar: lo que se grafica o se compara tiene que tener semantica.
 *
 * Unidades en codigos UCUM (ASCII). La primera de cada lista es la canonica: el
 * servidor guarda el valor tal como se capturo Y su conversion a la canonica,
 * para comparar sin mezclar kilos con libras.
 *
 * Los rangos son LIMITES DE CAPTURA para atrapar errores de tecleo (un peso de
 * 6500 kg), no rangos de normalidad ni clasificaciones clinicas. Pendientes de
 * revision clinica, como el resto de los catalogos (docs/VALIDACION.md §2).
 */
export const UNIDADES = [
    'kg',
    '[lb_av]',
    'cm',
    'm',
    '[in_i]',
    'mm[Hg]',
    '/min',
    'Cel',
    '[degF]',
    '%',
    'mg/dL',
    'mmol/L',
    // Oftalmologia (F6.1). Las de llaves son anotaciones UCUM sin dimension.
    '[diop]',
    '{decimal}',
    '{logMAR}',
    '{Snellen}',
];
/** Como se escribe cada unidad en pantalla. */
export const SIMBOLO_DE_UNIDAD = {
    kg: 'kg',
    '[lb_av]': 'lb',
    cm: 'cm',
    m: 'm',
    '[in_i]': 'in',
    'mm[Hg]': 'mmHg',
    '/min': 'por minuto',
    Cel: '°C',
    '[degF]': '°F',
    '%': '%',
    'mg/dL': 'mg/dL',
    'mmol/L': 'mmol/L',
    '[diop]': 'D',
    '{decimal}': 'decimal',
    '{logMAR}': 'logMAR',
    '{Snellen}': 'fracción de Snellen',
};
const CONVERSIONES = {
    '[lb_av]': { canonica: 'kg', factor: 0.45359237, desplazamiento: 0 },
    m: { canonica: 'cm', factor: 100, desplazamiento: 0 },
    '[in_i]': { canonica: 'cm', factor: 2.54, desplazamiento: 0 },
    '[degF]': { canonica: 'Cel', factor: 5 / 9, desplazamiento: -160 / 9 },
    'mmol/L': { canonica: 'mg/dL', factor: 18.016, desplazamiento: 0 },
};
export const FORMAS_DE_VALOR = ['cantidad', 'componentes'];
/** Un ojo, nunca los dos: lo que se mide por ojo (F6.1). */
const OJOS = ['derecha', 'izquierda'];
/**
 * Catalogos de metodo (F6.1). Nombran tecnicas de medicion, no valores ni
 * umbrales; esperan la revision clinica del paquete (F6.4).
 */
const METODOS_DE_TONOMETRIA = [
    { clave: 'aplanacion-goldmann', nombre: 'Aplanación (Goldmann)' },
    { clave: 'aplanacion-portatil', nombre: 'Aplanación portátil' },
    { clave: 'sin-contacto', nombre: 'Sin contacto (aire)' },
    { clave: 'rebote', nombre: 'Rebote' },
    { clave: 'electronica-portatil', nombre: 'Electrónica portátil' },
    { clave: 'otro', nombre: 'Otro (descríbalo en la nota)' },
];
const CONDICIONES_DE_AGUDEZA = [
    { clave: 'lejos-sin-correccion', nombre: 'Lejos, sin corrección' },
    { clave: 'lejos-con-correccion', nombre: 'Lejos, con corrección' },
    { clave: 'lejos-estenopeico', nombre: 'Lejos, con estenopeico' },
    { clave: 'cerca-sin-correccion', nombre: 'Cerca, sin corrección' },
    { clave: 'cerca-con-correccion', nombre: 'Cerca, con corrección' },
];
const METODOS_DE_REFRACCION = [
    { clave: 'subjetiva', nombre: 'Subjetiva' },
    { clave: 'autorrefractometro', nombre: 'Autorrefractómetro' },
    { clave: 'retinoscopia', nombre: 'Retinoscopía' },
    { clave: 'bajo-cicloplejia', nombre: 'Bajo cicloplejía' },
    { clave: 'lensometria', nombre: 'Lensometría (lentes en uso)' },
];
export const CODIGOS_DE_OBSERVACION = [
    'peso',
    'talla',
    'cintura',
    'presion-arterial',
    'frecuencia-cardiaca',
    'frecuencia-respiratoria',
    'temperatura',
    'saturacion-oxigeno',
    'glucosa-capilar',
    'presion-intraocular',
    'agudeza-visual-decimal',
    'agudeza-visual-logmar',
    'agudeza-visual-snellen',
    'refraccion',
    // Obesidad y diabetes (F6.6)
    'hemoglobina-glucosilada',
];
export const DEFINICION_DE_OBSERVACION = {
    peso: {
        nombre: 'Peso',
        forma: 'cantidad',
        unidades: ['kg', '[lb_av]'],
        lateralidad: 'no-aplica',
        rango: { min: 0.2, max: 500 },
    },
    talla: {
        nombre: 'Talla',
        forma: 'cantidad',
        unidades: ['cm', 'm', '[in_i]'],
        lateralidad: 'no-aplica',
        rango: { min: 20, max: 260 },
    },
    cintura: {
        nombre: 'Circunferencia de cintura',
        forma: 'cantidad',
        unidades: ['cm', '[in_i]'],
        lateralidad: 'no-aplica',
        rango: { min: 20, max: 250 },
    },
    'presion-arterial': {
        nombre: 'Presión arterial',
        forma: 'componentes',
        unidades: ['mm[Hg]'],
        lateralidad: 'no-aplica',
        componentes: [
            { clave: 'sistolica', nombre: 'Sistólica', rango: { min: 40, max: 300 } },
            { clave: 'diastolica', nombre: 'Diastólica', rango: { min: 20, max: 200 } },
        ],
    },
    'frecuencia-cardiaca': {
        nombre: 'Frecuencia cardiaca',
        forma: 'cantidad',
        unidades: ['/min'],
        lateralidad: 'no-aplica',
        rango: { min: 20, max: 300 },
    },
    'frecuencia-respiratoria': {
        nombre: 'Frecuencia respiratoria',
        forma: 'cantidad',
        unidades: ['/min'],
        lateralidad: 'no-aplica',
        rango: { min: 4, max: 80 },
    },
    temperatura: {
        nombre: 'Temperatura',
        forma: 'cantidad',
        unidades: ['Cel', '[degF]'],
        lateralidad: 'no-aplica',
        rango: { min: 25, max: 45 },
    },
    'saturacion-oxigeno': {
        nombre: 'Saturación de oxígeno',
        forma: 'cantidad',
        unidades: ['%'],
        lateralidad: 'no-aplica',
        rango: { min: 50, max: 100 },
    },
    'glucosa-capilar': {
        nombre: 'Glucosa capilar',
        forma: 'cantidad',
        unidades: ['mg/dL', 'mmol/L'],
        lateralidad: 'no-aplica',
        rango: { min: 10, max: 1500 },
    },
    'presion-intraocular': {
        nombre: 'Presión intraocular',
        forma: 'cantidad',
        unidades: ['mm[Hg]'],
        lateralidad: 'requerida',
        rango: { min: 0, max: 80 },
        lados: OJOS,
        metodos: METODOS_DE_TONOMETRIA,
    },
    // Agudeza visual (F6.1): una observacion por notacion. No se convierte entre
    // notaciones; la equivalencia no es lineal y la decide quien la lee. Lo que no
    // es numero (cuenta dedos, movimiento de manos, percepcion de luz) va en la
    // nota o en la plantilla, no aqui. «Bilateral» es la agudeza binocular.
    'agudeza-visual-decimal': {
        nombre: 'Agudeza visual (decimal)',
        forma: 'cantidad',
        unidades: ['{decimal}'],
        lateralidad: 'requerida',
        rango: { min: 0, max: 3 },
        metodos: CONDICIONES_DE_AGUDEZA,
    },
    'agudeza-visual-logmar': {
        nombre: 'Agudeza visual (logMAR)',
        forma: 'cantidad',
        unidades: ['{logMAR}'],
        lateralidad: 'requerida',
        rango: { min: -0.5, max: 3 },
        metodos: CONDICIONES_DE_AGUDEZA,
    },
    'agudeza-visual-snellen': {
        nombre: 'Agudeza visual (Snellen)',
        forma: 'componentes',
        unidades: ['{Snellen}'],
        lateralidad: 'requerida',
        componentes: [
            { clave: 'numerador', nombre: 'Distancia de prueba', rango: { min: 1, max: 100 } },
            { clave: 'denominador', nombre: 'Línea leída', rango: { min: 1, max: 4000 } },
        ],
        metodos: CONDICIONES_DE_AGUDEZA,
    },
    refraccion: {
        nombre: 'Refracción',
        forma: 'componentes',
        unidades: ['[diop]'],
        lateralidad: 'requerida',
        // Esfera y cilindro en dioptrias; el eje, en grados, viaja con ellos.
        componentes: [
            { clave: 'esfera', nombre: 'Esfera (D)', rango: { min: -40, max: 40 } },
            { clave: 'cilindro', nombre: 'Cilindro (D)', rango: { min: -20, max: 20 } },
            { clave: 'eje', nombre: 'Eje (°)', rango: { min: 0, max: 180 } },
        ],
        lados: OJOS,
        metodos: METODOS_DE_REFRACCION,
    },
    // F6.6: en porcentaje (NGSP), sin conversion a mmol/mol (IFCC) por ahora. El
    // rango es de captura, no de referencia: no dice si un valor esta bien o mal.
    'hemoglobina-glucosilada': {
        nombre: 'Hemoglobina glucosilada (HbA1c)',
        forma: 'cantidad',
        unidades: ['%'],
        lateralidad: 'no-aplica',
        rango: { min: 2, max: 25 },
    },
};
/**
 * Valor en la unidad canonica de la observacion, o `null` si la unidad no es de
 * esa observacion. Nunca convierte entre magnitudes distintas.
 */
export function aUnidadCanonica(codigo, valor, unidad) {
    const definicion = DEFINICION_DE_OBSERVACION[codigo];
    if (!definicion.unidades.includes(unidad))
        return null;
    const canonica = definicion.unidades[0];
    if (unidad === canonica)
        return valor;
    const conversion = CONVERSIONES[unidad];
    if (!conversion || conversion.canonica !== canonica)
        return null;
    return valor * conversion.factor + conversion.desplazamiento;
}
export function esCodigoDeObservacion(valor) {
    return CODIGOS_DE_OBSERVACION.includes(valor);
}
/**
 * Formula del IMC, versionada (plan §5.3). Si cambia (redondeo, unidades), sube
 * la version: un IMC guardado dentro de una nota firmada dice con que formula se
 * calculo y con que entradas.
 */
export const FORMULA_IMC = { codigo: 'imc', version: 1 };
/** IMC v1: kg / m², redondeado a un decimal. Entradas en unidades canonicas. */
export function imcDesdeCanonicos(pesoKg, tallaCm) {
    if (!Number.isFinite(pesoKg) || !Number.isFinite(tallaCm) || pesoKg <= 0 || tallaCm <= 0) {
        return null;
    }
    const metros = tallaCm / 100;
    const imc = pesoKg / (metros * metros);
    return Number.isFinite(imc) ? Math.round(imc * 10) / 10 : null;
}
//# sourceMappingURL=observaciones.js.map